import React from 'react'
import {BankAccount, BankTransaction} from '../types'

const money = (n: number) =>
  new Intl.NumberFormat('en-AU', {style: 'currency', currency: 'AUD'}).format(n)
const day = (s: string) =>
  new Date(`${s}T12:00:00`).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
export const needsBankReview = (t: BankTransaction) => !t.reconciliationId && !t.reviewedAt

export function BankTransactionFeed({
  rows,
  accounts,
  selected,
  onSelect,
  checked,
  onCheck,
  onDismiss,
  busy,
  loading,
  hasStatements,
}: {
  rows: BankTransaction[]
  accounts: BankAccount[]
  selected?: string
  onSelect: (id: string) => void
  checked: string[]
  onCheck: (ids: string[]) => void
  onDismiss: (ids: string[]) => void
  busy: boolean
  loading: boolean
  hasStatements: boolean
}) {
  const eligible = rows.filter(needsBankReview)
  const selectable = eligible.slice(0, 100)
  const selectedIds = checked.filter((id) => eligible.some((t) => t.id === id))
  const transfers = eligible.filter((t) => t.category === 'internal_transfer').slice(0, 100)
  return (
    <div className='bank-feed'>
      {!!eligible.length && (
        <div className='bank-feed-actions'>
          <label className='bank-check'>
            <input
              type='checkbox'
              aria-label='Select up to 100 visible transactions'
              disabled={busy}
              checked={!!selectable.length && selectable.every((t) => selectedIds.includes(t.id))}
              ref={(input) => {
                if (input)
                  input.indeterminate =
                    selectedIds.length > 0 && !selectable.every((t) => selectedIds.includes(t.id))
              }}
              onChange={(e) => onCheck(e.target.checked ? selectable.map((t) => t.id) : [])}
            />
            {selectedIds.length
              ? `${selectedIds.length} selected`
              : eligible.length > 100
              ? 'Select first 100'
              : 'Select visible'}
          </label>
          {!!transfers.length && (
            <button
              className='bank-link'
              disabled={busy}
              onClick={() => onCheck(transfers.map((t) => t.id))}
            >
              Select transfers ({transfers.length})
            </button>
          )}
          {!!selectedIds.length && (
            <>
              <button
                className='btn btn-sm btn-light'
                disabled={busy}
                onClick={() => onDismiss(selectedIds)}
              >
                Dismiss selected ({selectedIds.length})
              </button>
              <button className='bank-link' disabled={busy} onClick={() => onCheck([])}>
                Clear selection
              </button>
            </>
          )}
          <small>Dismissed entries stay in Cashflow. Find them under Reviewed.</small>
        </div>
      )}
      <div className='bank-lines' aria-label='Bank transactions'>
        {loading && !rows.length ? (
          <p className='p-4'>Loading statements…</p>
        ) : !rows.length ? (
          <div className='bank-empty'>
            <h3>
              {hasStatements ? 'No transactions in this view' : 'Start with an ANZ statement'}
            </h3>
            <p>
              {hasStatements
                ? 'Try another money or status filter. Dismissed entries are in Reviewed.'
                : 'Import your ANZ transaction CSV to find matching bills.'}
            </p>
          </div>
        ) : (
          rows.map((t) => (
            <div key={t.id} className={`bank-feed-row ${selected === t.id ? 'selected' : ''}`}>
              {needsBankReview(t) && (
                <input
                  className='bank-row-check'
                  type='checkbox'
                  aria-label={`Select transaction ${t.descriptionRaw}`}
                  checked={selectedIds.includes(t.id)}
                  disabled={busy || (selectedIds.length >= 100 && !selectedIds.includes(t.id))}
                  onChange={(e) =>
                    onCheck(
                      e.target.checked
                        ? [...selectedIds, t.id]
                        : selectedIds.filter((id) => id !== t.id)
                    )
                  }
                />
              )}
              <button
                className='bank-line'
                aria-pressed={selected === t.id}
                onClick={() => onSelect(t.id)}
              >
                <span>
                  {day(t.date)} · {accounts.find((a) => a.id === t.accountId)?.displayName}
                </span>
                <strong>{t.descriptionRaw}</strong>
                <div>
                  <b className={t.amount < 0 ? 'out' : 'in'}>{money(t.amount)}</b>
                  <small>
                    {t.reconciliationId
                      ? 'Reconciled'
                      : t.reviewedAt
                      ? t.reviewKind === 'dismissed'
                        ? 'Dismissed'
                        : 'Reviewed'
                      : t.category === 'internal_transfer'
                      ? 'Transfer to review'
                      : 'Needs review'}
                  </small>
                </div>
              </button>
              {needsBankReview(t) && (
                <button
                  className='bank-dismiss'
                  disabled={busy}
                  title='Dismiss from bill reconciliation'
                  aria-label={`Dismiss ${t.descriptionRaw}`}
                  onClick={() => onDismiss([t.id])}
                >
                  <span aria-hidden='true'>×</span>
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
